import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, MoreHorizontal, UserPlus, Ban, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import adminClient from '@/lib/api/adminClient';
import { formatDate } from '@/lib/utils';

const PLAN_BADGE: Record<string, 'default' | 'secondary' | 'success' | 'warning' | 'outline'> = {
  trial: 'secondary',
  starter: 'outline',
  pro: 'success',
  expert: 'warning',
};

export function UsersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', page, search, planFilter, statusFilter],
    queryFn: () =>
      adminClient.get('/admin/users', {
        params: {
          page,
          per_page: 20,
          email_contains: search || undefined,
          plan: planFilter !== 'all' ? planFilter : undefined,
          is_active: statusFilter === 'all' ? undefined : statusFilter === 'active',
        },
      }).then((r) => r.data),
  });

  const suspendMutation = useMutation({
    mutationFn: (userId: number) =>
      adminClient.post(`/admin/users/${userId}/suspend`, { reason: 'Admin action', permanent: false }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });

  const activateMutation = useMutation({
    mutationFn: (userId: number) => adminClient.post(`/admin/users/${userId}/activate`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">用戶管理</h1>
        <span className="text-sm text-muted-foreground">總計：{data?.total ?? 0} 位用戶</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="搜尋電子郵件..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9"
          />
        </div>
        <Select value={planFilter} onValueChange={(v) => { setPlanFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="方案" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">所有方案</SelectItem>
            <SelectItem value="trial">試用版</SelectItem>
            <SelectItem value="starter">入門版</SelectItem>
            <SelectItem value="pro">專業版</SelectItem>
            <SelectItem value="expert">專家版</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="狀態" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">所有狀態</SelectItem>
            <SelectItem value="active">啟用中</SelectItem>
            <SelectItem value="suspended">已停權</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>電子郵件</TableHead>
                  <TableHead>名稱</TableHead>
                  <TableHead>方案</TableHead>
                  <TableHead>點數</TableHead>
                  <TableHead>狀態</TableHead>
                  <TableHead>註冊日期</TableHead>
                  <TableHead className="w-[80px]">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.items || []).map((user: any) => (
                  <TableRow key={user.id} className="cursor-pointer" onClick={() => navigate(`/users/${user.id}`)}>
                    <TableCell className="font-medium">{user.email}</TableCell>
                    <TableCell>{user.full_name || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={PLAN_BADGE[user.plan] || 'secondary'}>
                        {user.plan || 'trial'}
                      </Badge>
                    </TableCell>
                    <TableCell>{user.credits_balance.toFixed(0)}</TableCell>
                    <TableCell>
                      <Badge variant={user.is_active ? 'success' : 'destructive'}>
                        {user.is_active ? '啟用中' : '已停權'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">{formatDate(user.created_at)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {user.is_active ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => suspendMutation.mutate(user.id)}
                          title="停權"
                        >
                          <Ban className="w-4 h-4 text-destructive" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => activateMutation.mutate(user.id)}
                          title="啟用"
                        >
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.total > 20 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            上一頁
          </Button>
          <span className="text-sm text-muted-foreground">
            第 {page} 頁，共 {Math.ceil(data.total / 20)} 頁
          </span>
          <Button variant="outline" size="sm" disabled={page >= Math.ceil(data.total / 20)} onClick={() => setPage(page + 1)}>
            下一頁
          </Button>
        </div>
      )}
    </div>
  );
}
